/* eslint-disable i18n/no-russian-character */
import { useTranslations } from 'next-intl';
import { useState, useMemo } from 'react';

import { AddressFragment } from '@/fo/api/magento';
import {
  FeatureCityModalBase,
  Suggestion,
  INITIAL_CITIES as citiesDictionary
} from '@/fo/feature/city/modal/base';
import { type ProductDeliveryData } from '@/fo/feature/product/info/main';
import { Map, type StorePoint } from '@/fo/feature/shops/page/base';
import { UiKitListCharacteristic } from '@/fo/ui/kit/list/characteristic';
import { utilPluralize } from '@/fo/util/pluralize';

import { styles } from './ShippingInfo.styles';

interface Location {
  region: string;
  city: string;
  cityPure: string;
  center?: [number, number];
}

interface ShippingInfoProps {
  data?: {
    stores: StorePoint[];
    error?: string | null;
    productDeliveryData: ProductDeliveryData | null;
  };
  onSelectCity?: (city: string) => void;
  isLoading?: boolean;
}

export function ShippingInfo({
  data,
  onSelectCity,
  isLoading
}: ShippingInfoProps) {
  const t = useTranslations();
  const slots = styles();
  const stores = data ? data.stores : [];
  const productDeliveryData = data ? data.productDeliveryData : null;

  const pluralizeProducts = utilPluralize({
    one: t('pluralize.days.one'),
    few: t('pluralize.days.few'),
    many: t('pluralize.days.many')
  });

  const deliveryDays = productDeliveryData?.courier_day_from ?? 0;
  const shippingCharacteristics = [
    {
      label: `${t('Delivery')}`,
      value: `${t('Delivery cost from', { from: productDeliveryData?.courier_price || 0 })}`
    },
    {
      label: `${t('Delivery time')}`,
      value: `${t('from')} ${deliveryDays} ${pluralizeProducts(deliveryDays)}`
    }
  ];

  if (stores && stores.length) {
    shippingCharacteristics.push({
      label: `${t('Pickup')}`,
      value: `${t('Free')}`
    });
  }

  const location: Location = {
    region: 'Москва',
    city: 'Москва',
    cityPure: 'Москва'
  };

  const [modalState, setModalState] = useState<{
    open?: boolean;
    openLocation?: boolean;
    mode?: 'edit' | 'create' | 'delete';
    address?: AddressFragment;
    location?: Location;
  }>({});

  const handleSelectCity = () => {
    setModalState((s) => ({ ...s, openLocation: true }));
  };

  const selectCity = (city: Suggestion) => {
    if (onSelectCity) {
      onSelectCity(city?.value);
    }
    setModalState((s) => ({
      ...s,
      location: {
        city: city?.value,
        region: city.region,
        cityPure: city.cityPure
      }
    }));
  };

  const getInitialCities = (location: Location) => {
    const initialCities: Suggestion[] = [
      ...citiesDictionary.filter(
        (city) =>
          city.region &&
          city.region !== location.region &&
          city.value !== location.city
      )
    ];

    return initialCities;
  };
  const initialCities = useMemo(
    () => getInitialCities(modalState.location || location),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalState.location]
  );

  const currentCity =
    (stores && !!stores.length && stores?.[0].city) ||
    modalState.location?.city ||
    location.city;

  return (
    <>
      <div className={slots.base()}>
        <div className={slots.topSection()}>
          <h2 className={slots.title()}>{t('Shipping and pickup')}</h2>

          {!!productDeliveryData && productDeliveryData.free_delivery_from && (
            <p className={slots.subtitle()}>
              {t('Free shipping from price', {
                priceCost: productDeliveryData.free_delivery_from
              })}
            </p>
          )}
        </div>

        <div className={slots.shippingCharacteristics()}>
          <p className={slots.shippingCharacteristicsTitle()}>
            {t('Shipping price fo shipping from')}&nbsp;
            <span
              className={slots.shippingCity()}
              onClick={() => handleSelectCity()}
            >
              {currentCity}
            </span>
          </p>

          {!isLoading && (
            <UiKitListCharacteristic
              items={shippingCharacteristics}
              className={slots.characteristic()}
              classNameLabel={slots.characteristicLabel()}
              classNameValue={slots.characteristicValue()}
              classNameItem={slots.characteristicItem()}
            />
          )}

          {isLoading && <span className={slots.loading()}></span>}
        </div>

        {!isLoading && (
          <>
            {stores && !!stores.length ? (
              <div className={slots.pickupPoints()}>
                <div className={slots.pickupPointsTitle()}>
                  {t('Availability in shops')}
                </div>
                <Map
                  points={stores}
                  selectedCity={currentCity}
                  zoom={10}
                />
              </div>
            ) : (
              <div className={slots.shippingCharacteristics()}>
                <p className={slots.shippingCharacteristicsTitle()}>
                  {t('No pickup text in delivery popup')}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <FeatureCityModalBase
        mode="address"
        open={Boolean(modalState.openLocation)}
        onSelectCity={selectCity}
        className="z-[11100]"
        setOpen={(open) => {
          if (!open) {
            setModalState((s) => ({ ...s, openLocation: false }));
          }
        }}
        initialCities={initialCities}
      />
    </>
  );
}
